import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError, downloadPdfFile } from "../lib/api";
import RoadmapView from "../components/RoadmapView";
import PdfGateDialog, { isVerifiedUser } from "../components/PdfGateDialog";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import ChatPanel from "../components/ChatPanel";
import { track } from "../lib/analytics";
import { FileDown } from "lucide-react";

const deriveName = (data) => {
  const a = data.answers || {};
  return data.project_name || [a.capacity_mw ? `${a.capacity_mw} MW` : "", a.project_type?.replace(/_/g, " "), a.state].filter(Boolean).join(" — ");
};

export default function RoadmapPage() {
  const { runId } = useParams();
  const [run, setRun] = useState(null);
  const [error, setError] = useState("");
  const [saveOpen, setSaveOpen] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);
  const [projName, setProjName] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const pendingDownload = useRef(false);
  const verified = isVerifiedUser(user);

  const fetchRun = useCallback(async () => {
    try {
      const { data } = await api.get(`/runs/${runId}`);
      setRun(data);
      setProjName(deriveName(data));
    } catch (e) {
      setError(apiError(e));
    }
  }, [runId]);

  useEffect(() => {
    fetchRun();
  }, [fetchRun]);

  const startDownload = async () => {
    setPdfBusy(true);
    try {
      await downloadPdfFile(`/runs/${runId}/pdf`, `RegNav - ${projName || "roadmap"}.pdf`);
      toast.success("PDF downloaded — a Solarian-branded report");
      track("pdf_downloaded", { source: "roadmap" });
    } catch {
      toast.error("PDF couldn't be generated — try again", { action: { label: "Retry", onClick: startDownload } });
    } finally {
      setPdfBusy(false);
    }
  };

  const openGate = (source) => {
    pendingDownload.current = true;
    setGateOpen(true);
    track("pdf_gate_shown", { source });
  };

  const handleDownloadPdf = () => {
    if (!verified) return openGate("download_button");
    startDownload();
  };

  const onGateVerified = () => {
    if (pendingDownload.current) {
      pendingDownload.current = false;
      toast.success("Verified — preparing your PDF");
      startDownload();
    }
  };

  const doSave = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/projects", { run_id: runId, name: projName });
      toast.success("Project saved");
      track("project_saved");
      navigate(`/projects/${data.id}`);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (error) return (
    <div className="max-w-2xl mx-auto px-4 py-20 text-center">
      <p className="text-[#0B1F3A] font-serif text-xl">{error}</p>
      <button onClick={() => navigate("/wizard")} className="mt-4 bg-[#1D4ED8] text-white rounded-md px-5 py-2.5 text-sm font-medium" data-testid="roadmap-error-wizard-btn">Open the wizard</button>
    </div>
  );

  if (!run) return (
    <div className="max-w-5xl mx-auto px-4 py-10 space-y-4">
      <div className="h-48 bg-[#0B1F3A]/10 rounded-xl animate-pulse" />
      <div className="h-32 bg-[#0B1F3A]/10 rounded-xl animate-pulse" />
      <div className="h-64 bg-[#0B1F3A]/10 rounded-xl animate-pulse" />
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      {!verified && (
        <div className="mb-5 bg-white border border-[#059669]/30 rounded-xl px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="pdf-lead-card">
          <div className="flex items-start gap-3">
            <FileDown size={18} className="text-[#059669] mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-[#0B1F3A]">Want this as a branded PDF to share with your team?</p>
              <p className="text-xs text-[#475569] mt-0.5">One-time email + mobile verification — your roadmap below stays fully visible either way.</p>
            </div>
          </div>
          <button onClick={() => openGate("lead_card")} className="shrink-0 bg-[#059669] text-white rounded-md px-4 py-2.5 text-sm font-bold hover:bg-[#047857]" data-testid="pdf-lead-verify-btn">
            Verify to download
          </button>
        </div>
      )}
      <RoadmapView roadmap={run.roadmap} name={projName} runId={runId} onSave={() => setSaveOpen(true)} onDownloadPdf={handleDownloadPdf} pdfBusy={pdfBusy} />
      <div className="mt-6"><ChatPanel contextId={runId} /></div>
      <PdfGateDialog open={gateOpen} onOpenChange={setGateOpen} onVerified={onGateVerified} />
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent aria-describedby={undefined} data-testid="save-project-dialog">
          <DialogHeader><DialogTitle className="font-serif">Save this project</DialogTitle></DialogHeader>
          <form onSubmit={doSave} className="space-y-3">
            <input value={projName} onChange={(e) => setProjName(e.target.value)} required placeholder="Project name"
              className="w-full border border-[#0B1F3A]/20 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#059669] focus:outline-none" data-testid="save-project-name-input" />
            <button type="submit" className="w-full bg-[#1D4ED8] text-white rounded-md py-2.5 text-sm font-medium" data-testid="save-project-confirm-btn">Save to my projects</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
